import { LitElement, PropertyValues, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { AtlTooltipProps } from './props';
import * as TagNames from './tooltip.tagnames';

type BootstrapTooltip = {
   dispose: () => void;
   _activeTrigger: () => void;
   _element: unknown;
};

@customElement(TagNames.Tooltip)
export class AtlTooltip extends LitElement implements AtlTooltipProps {
   private _tooltipInstance?: BootstrapTooltip;

   declare placement?: 'top' | 'bottom' | 'left' | 'right';
   declare disabledTooltip?: boolean;

   static properties = {
      placement: { type: String },
      disabledTooltip: { type: Boolean },
   };

   static styles = css`
      slot[name='tooltip-content'] {
         display: none;
      }
   `;

   override disconnectedCallback(): void {
      this._removeTooltip();
      super.disconnectedCallback();
   }

   protected override updated(changedProperties: PropertyValues): void {
      super.updated(changedProperties);

      this._removeTooltip();
      this._applyTooltip();
   }

   private getTooltipContent() {
      const tooltipContentSlot = this.shadowRoot?.querySelector(
         'slot[name="tooltip-content"]'
      ) as HTMLSlotElement;
      const contentElements = tooltipContentSlot?.assignedNodes({
         flatten: false,
      });
      const container = document.createElement('div');
      contentElements?.forEach((node) => {
         container.appendChild(node.cloneNode(true));
      });
      return container.innerHTML;
   }

   private _applyTooltip() {
      if (this.disabledTooltip) return;

      const slot = this.shadowRoot?.querySelector(
         'slot:not([name])'
      ) as HTMLSlotElement;
      const assignedNodes = slot?.assignedNodes({
         flatten: true,
      });
      const targetElement = assignedNodes?.find(
         (node) => node.nodeType === Node.ELEMENT_NODE
      ) as HTMLElement | undefined;
      if (targetElement) {
         targetElement.setAttribute('data-bs-toggle', 'tooltip');
         this._tooltipInstance = new bootstrap.Tooltip(
            targetElement as HTMLElement,
            {
               placement: this.placement || 'bottom',
               html: true,
               title: this.getTooltipContent(),
            }
         );
      }
   }

   private _removeTooltip() {
      if (this._tooltipInstance) {
         this._tooltipInstance.dispose();
         // see https://github.com/twbs/bootstrap/issues/37474
         this._tooltipInstance._activeTrigger = () => {};
         this._tooltipInstance._element = document.createElement('noscript');
         this._tooltipInstance = undefined;
      }
   }

   private _onSlotChange() {
      this._removeTooltip();
      this._applyTooltip();
   }

   render() {
      return html`
         <slot @slotchange=${this._onSlotChange}></slot>
         <slot name="tooltip-content"></slot>
      `;
   }
}
